import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { CompanyGroup } from '../../administration/company-group/company-group';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { CompanyGroupService } from '../../administration/company-group/company-group.service';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';

@Component({
    selector: 'kiene-company-group-autocomplete',
    templateUrl: './company-group-autocomplete.component.html',
    styleUrls: ['./company-group-autocomplete.component.scss'],
})
export class CompanyGroupAutocompleteComponent implements OnInit, OnChanges {
    @ViewChild('companyGroupInput') companyGroupInput: MatInput;
    @ViewChild('companyGroupTrigger') autocompleteTrigger: MatAutocompleteTrigger;
    @ViewChild('companyGroupFormField', { read: ElementRef }) companyGroupFormField: ElementRef<HTMLElement>;

    companyGroupCtrl = new UntypedFormControl();
    companyGroups: Observable<CompanyGroup[]>;
    panelWidthPx: string;

    @Output('selectedCompanyGroup')
    selectedCompanyGroup: EventEmitter<CompanyGroup> = new EventEmitter<CompanyGroup>();

    @Input('companyGroup') companyGroup = new CompanyGroup();

    constructor(
        private service: CompanyGroupService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.autocompleteCompanyGroup();
    }

    ngOnChanges() {
        if (this.companyGroupCtrl && this.companyGroup.unternehmensgruppe_id) {
            this.companyGroupCtrl.setValue(this.companyGroup);
        }
    }

    autocompleteCompanyGroup() {
        this.companyGroups = this.companyGroupCtrl.valueChanges.pipe(
            startWith(''),
            switchMap(value => {
                return this.searchCompanyGroups(value);
            }),
        );
    }

    searchCompanyGroups(value: string) {
        return this.service.searchCompanyGroups(value).pipe(
            debounceTime(300),
            distinctUntilChanged(),
            map(response => response.records),
        );
    }

    companyGroupMapper(cg: CompanyGroup) {
        if (cg) {
            return cg.bezeichnung;
        }
        return '';
    }

    companyGroupSelected(event: MatAutocompleteSelectedEvent) {
        const companyGroup = event.option.value;
        this.selectedCompanyGroup.emit(companyGroup);
    }

    public setFocus() {
        this.preparePanelLayout();
        this.companyGroupInput?.focus();
    }

    onAutocompleteOpened(): void {
        this.schedulePanelLayoutSync();
    }

    private preparePanelLayout(): void {
        this.syncPanelWidth();
        this.changeDetectorRef.detectChanges();
    }

    private schedulePanelLayoutSync(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.syncPanelWidth();
                this.applyPanelLayout();
                this.autocompleteTrigger?.updatePosition();
                this.changeDetectorRef.detectChanges();
            });
        });
    }

    private syncPanelWidth(): void {
        const fieldEl = this.companyGroupFormField?.nativeElement;
        const width = fieldEl?.getBoundingClientRect().width;
        if (width && width > 0) {
            this.panelWidthPx = `${Math.round(width)}px`;
        }
    }

    private applyPanelLayout(): void {
        const panel = this.autocompleteTrigger?.autocomplete?.panel?.nativeElement as HTMLElement | undefined;
        if (!panel || !this.panelWidthPx) {
            return;
        }
        const overlayPane = panel.closest('.cdk-overlay-pane') as HTMLElement | null;
        if (overlayPane) {
            overlayPane.style.width = this.panelWidthPx;
            overlayPane.style.minWidth = this.panelWidthPx;
            overlayPane.style.maxWidth = this.panelWidthPx;
        }
        panel.style.width = '100%';
        panel.style.boxSizing = 'border-box';
    }
}
